import qrcode from "qrcode-generator";

/**
 * The QR module grid, built ONCE from a payload and consumed by every
 * renderer — the web screen, the print/thermal document, and the
 * server-generated PDF.
 *
 * This exists so "the PDF's QR matches the screen's QR" is true by
 * construction, not by two implementations happening to agree today. Two
 * copies of a module-path builder are two places for a coordinate to drift;
 * one function used by every surface cannot drift from itself.
 */

export interface QrModuleGrid {
  /** Modules per side, before the quiet zone. */
  readonly moduleCount: number;
  /** Quiet-zone width in modules — part of the symbol, not decoration. */
  readonly quiet: number;
  /** Total viewBox side length: moduleCount + quiet * 2. */
  readonly span: number;
  /** One SVG path `d` covering every dark module, in a 1-unit-per-module grid. */
  readonly path: string;
}

/**
 * Builds the module grid for a ZATCA QR payload (or any Base64/text payload).
 *
 * Error correction "M": the standard choice for a printed document — it
 * survives a smudge or a fold without inflating the module count the way
 * "Q"/"H" would, which matters on a 58mm receipt where every module is a
 * fraction of a millimetre.
 */
export function buildQrModuleGrid(payload: string): QrModuleGrid {
  const qr = qrcode(0, "M");
  qr.addData(payload, "Byte");
  qr.make();

  const moduleCount = qr.getModuleCount();
  const quiet = 4;

  const parts: string[] = [];
  for (let r = 0; r < moduleCount; r++) {
    for (let c = 0; c < moduleCount; c++) {
      if (qr.isDark(r, c)) parts.push(`M${c + quiet} ${r + quiet}h1v1h-1z`);
    }
  }

  return { moduleCount, quiet, span: moduleCount + quiet * 2, path: parts.join("") };
}

/**
 * The grid as a complete, self-contained SVG string — white ground, black
 * modules, no external stylesheet dependency. Used by the server-side PDF
 * template, which has no CSS pipeline to lean on the way the web component
 * does.
 */
export function buildQrSvg(payload: string, sizePx: number): string {
  const grid = buildQrModuleGrid(payload);
  return (
    `<svg viewBox="0 0 ${grid.span} ${grid.span}" width="${sizePx}" height="${sizePx}" ` +
    `xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">` +
    `<rect width="${grid.span}" height="${grid.span}" fill="#ffffff"/>` +
    `<path d="${grid.path}" fill="#000000"/>` +
    `</svg>`
  );
}
